<template>
  <b-container fluid>
    <center><h1 class='my-2'>Search Sales Orders</h1></center>
    <b-form @submit=onSubmit @reset=onReset autocomplete="off" v-if=showForm>
      <b-form-row class='justify-content-center'>
        <b-col xl=6>
          <b-card title="Order Details">
            <order-form-input label="Order ID" waIcon fbIcon shopifyIcon draftIcon v-model='searchForm.orderNumber' />
            <order-form-input label="Name" fbIcon v-model='searchForm.name' />
            <order-form-input label="E-Mail" waIcon fbIcon shopifyIcon draftIcon v-model='searchForm.email' />
            <order-form-input label="Total Price" waIcon fbIcon v-model='searchForm.totalPrice' />
            <order-form-input label="Purchase Order" waIcon fbIcon v-model='searchForm.customerPurchaseOrder' />
            <order-form-input label="Team Number" waIcon v-model='searchForm.teamNumber' />
            <order-form-input label="CC Last 4 Digits" waIcon shopifyIcon v-model='searchForm.ccLastFour' />
            <order-form-input label="IP Address" waIcon v-model='searchForm.ipAddress' />
          </b-card>
        </b-col>
        <b-col xl=6>
          <b-card title="Address Details">
            <order-form-input label=Street fbIcon v-model='searchForm.street' />
            <order-form-input label=City fbIcon v-model='searchForm.city' />
            <order-form-input label=Zip fbIcon v-model='searchForm.zip' />
            <order-form-input label=State fbIcon v-model='searchForm.state' />
            <order-form-input label=Country fbIcon v-model='searchForm.country' />
          </b-card>
        </b-col>
      </b-form-row>
      <b-row align-h=around class='mt-2'>
        <b-col cols=2 />
        <b-col cols=*>
          <b-btn type=reset variant=danger block>Reset</b-btn>
        </b-col>
        <b-col cols=*>
          <b-btn type=submit variant=primary block>Search</b-btn>
        </b-col>
        <b-col cols=2 />
      </b-row>
    </b-form>
    <section v-else>
      <b-btn block variant=primary @click="showForm=true" class='mb-2'>
        &lt;&lt;&lt; Back to Search
      </b-btn>
      <div v-if="!this.busy">
        <b-row class='align-items-center'>
          <b-col lg=4>
            <center><h4>{{count > maxCount ? maxCount+"+" : count}} Results</h4></center>
          </b-col>
          <b-col>
            <b-alert :show="count > maxCount" variant=danger>We found too many orders that match your search. This is only showing {{maxCount}} orders. Please narrow your search if you can't find the order you are looking for</b-alert>
          </b-col>
        </b-row>
      </div>
      <b-table :fields=fields :items=mergedOrders :busy=busy :sort-by="'createdAt'" :sort-desc="true" :sort-compare="sortCompare">
        <template slot='table-busy'>
          <center>
            <fingerprint-spinner :animation-duration="1500" :size="300" color="#293a93" class="align-middle" />
          </center>
        </template>
        <template slot=num slot-scope=row>
          <router-link target="_blank" :to="{ name: 'View Order', params: { id: row.item.num } }">
            {{ row.item.num }}
          </router-link>
          <fishbowl-status :fishbowlOrder=row.item.fishbowl />
          <web-status :websiteOrder="row.item.web" />
        </template>
        <template slot=createdAt slot-scope=row>
          <nice-date :value=mergeField(row.item.createdAt) />
        </template>
        <template slot=issuedAt slot-scope=row>
          <nice-date :value=mergeField(row.item.issuedAt) />
        </template>
        <template slot='email' slot-scope=row>
          <div v-if="hasCompany(row.item)">
            <data-merger :dict=row.item.company title="Billing Company" />
          </div>
          <div v-else>
            <data-merger :dict=row.item.firstName title="Billing First Name" /> <data-merger :dict=row.item.lastName title="Billing Last Name" />
          </div>
          <data-merger :dict='row.item.email' title="Email" />
        </template>
        <template slot='street1' slot-scope=row>
          <div v-if="hasCompany(row.item)">
            <data-merger :dict=row.item.firstName title="Billing First Name" /> <data-merger :dict=row.item.lastName title="Billing Last Name" />
          </div>
          <data-merger :dict=row.item.street1 title="Billing Street 1" />
        </template>
        <template slot='region' slot-scope=row>
          <data-merger :dict=row.item.region title="Billing Region" />
        </template>
        <template slot='zip' slot-scope=row>
          <data-merger :dict=row.item.zip title="Billing ZIP" />
        </template>
        <template slot='poNum' slot-scope=row>
          <data-merger :dict=row.item.poNum title="PO Number" fuzzy />
        </template>
        <template slot='totalPrice' slot-scope=row>
          <data-merger :dict=row.item.totalPrice formatter=formatDollars title="Total Price" />
        </template>
      </b-table>
    </section>
  </b-container>
</template>

<script>
import axios from 'axios'

import FishbowlStatus from './FishbowlStatus'
import WebStatus from './WebStatus'
import OrderFormInput from '../../utility/OrderFormInput';
import DataMerger from '../../utility/DataMerger';
import NiceDate from '../../utility/NiceDate';
import { FingerprintSpinner } from "epic-spinners";

import orderMerge from '../../../services/orderMerge';
import workareaService from '../../../services/workarea'
import fishbowlService from '../../../services/fishbowl'
import shopifyService from '../../../services/shopify'

const BASE_API = process.env.VUE_API_ROOT;

export default {
  name: 'Search',
  data() {
    return {
      showForm: true,
      searchForm: { },
      // sources,
      maxCount: 100,
      count: 0,
      busy: false,
      fields: [
        { key: 'num', label: 'Order ID', sortable: true },
        { key: 'createdAt', sortable: true },
        { key: 'issuedAt', sortable: true },
        { key: 'email', label: 'Customer', sortable: true },
        { key: 'street1', label: 'Street', sortable: true },
        { key: 'region', sortable: true },
        { key: 'zip', sortable: true },
        { key: 'poNum', label: 'PO#', sortable: true },
        { key: 'totalPrice', sortable: true },
      ],
      mergedOrders: [],
    }
  },
  components: {
    OrderFormInput,
    FishbowlStatus,
    WebStatus,
    DataMerger,
    NiceDate,
    FingerprintSpinner,
  },
  mounted() {
    this.onReset();
  },
  methods: {
    onSubmit(e) {
      if (e != null) e.preventDefault()
      this.busy = true
      this.showForm = false
      this.searchOrders(this.searchForm).then((response) => {
        this.mergeOrders(response)
      }).catch(error => console.error(error))
    },
    hasCompany(item) {
      return !!item.company && !!orderMerge.mergeField(item.company)
    },
    sortCompare(a, b, key) {
      let aVal = orderMerge.mergeField(this.fetchKey(a, key));
      let bVal = orderMerge.mergeField(this.fetchKey(b, key));
      if (key == 'totalPrice') {
        return Number(aVal) - Number(bVal)
      } else {
        return String(aVal).localeCompare(String(bVal), { numeric: true })
      }
    },
    fetchKey(obj, key) {
      switch(key) {
        case 'num': return ({ fishbowl: obj.num })
        default: return obj[key]
      }
    },
    async searchOrders(params) {
      let trimmedParams = JSON.parse(JSON.stringify(params));
      Object.keys(trimmedParams).forEach((key) => {
        trimmedParams[key] = trimmedParams[key].trim()
      })

      const [fishbowl, shopify, workarea] = await Promise.all([
        fishbowlService.searchOrder(trimmedParams),
        shopifyService.searchOrder(trimmedParams),
        workareaService.searchOrder(trimmedParams),
      ])
      return { fishbowl, shopify, workarea }
    },
    mergeOrders(search) {
      this.mergedOrders = [];
      if (Object.keys(search).some(k => !!search[k] && search[k].length > 0)) {
        let orders = []
        Object.keys(search).map(key => {
          switch (key) {
            case 'shopify': return search[key].map(o => ({ num: o.name, createdAt: o.createdAt }))
            case 'workarea': return search[key].map(o => ({ num: o.num, createdAt: o.createdAt }))
            case 'fishbowl': return search[key].map(o => ({ num: o.num, createdAt: o.createdAt }))
            default:
              console.warn(`Don't know how to handle orders from ${key}!`)
              return []
          }
        }).forEach(arr => {
          arr.forEach(o => {
            orders.push(o)
          })
        })
        orders = orders.sort(
          (a,b) => a.createdAt - b.createdAt
        ).filter( // only keep first search result for each num
          (value, index, array) => array.findIndex(item => item.num === value.num) == index
        )

        this.count = orders.length
        if (this.count === 1) {
          this.$router.push({ name: 'View Order', params: { id: orders[0].num }})
        } else {
          orders = orders.slice(0, this.maxCount)
          this.mergedOrders = orders.map(o => ({ num: o.num, web: {}, fishbowl: {} }))
          Promise.all(orders.map(async (o) => await axios.get(`${BASE_API}/bridge/${o.num}`).then(r => {
            const orderMatch = this.mergedOrders.find(o => o.num === r.data.merged.num)
            orderMatch.fishbowl = r.data.fishbowl
            orderMatch.web = r.data.web
            orderMatch.createdAt = r.data.merged.createdAt
            orderMatch.issuedAt = r.data.merged.issuedAt
            orderMatch.company = r.data.merged.billingAddress.company
            orderMatch.firstName = r.data.merged.billingAddress.firstName
            orderMatch.lastName = r.data.merged.billingAddress.lastName
            orderMatch.street1 = r.data.merged.billingAddress.street1
            orderMatch.region = r.data.merged.billingAddress.region
            orderMatch.zip = r.data.merged.billingAddress.zip
            orderMatch.email = r.data.merged.email
            orderMatch.poNum = r.data.merged.poNum
            orderMatch.totalPrice = r.data.merged.totalPrice
          }).catch(error => console.error(error))))
          this.busy = false
        }
      } else {
        console.warn('No results found from ANY SOURCE')
        this.busy = false
        this.error = true
      }
    },
    mergeField(dict) {
      return orderMerge.mergeField(dict)
    },
    onReset() {
      this.searchForm = {
        orderNumber: this.$route.query.orderNumber,
        email: this.$route.query.email,
        totalPrice: this.$route.query.totalPrice,
        customerPurchaseOrder: this.$route.query.customerPurchaseOrder,
        teamNumber: this.$route.query.teamNumber,
        ccLastFour: this.$route.query.ccLastFour,
        ipAddress: this.$route.query.ipAddress,
        name: this.$route.query.name,
        street: this.$route.query.street,
        city: this.$route.query.city,
        zip: this.$route.query.zip,
        state: this.$route.query.state,
        country: this.$route.query.country,
      };
      if (Object.keys(this.$route.query).length > 0) {
        this.onSubmit(null)
      }
    },
  }
}
</script>

<style scoped>
  .search-error {
    margin: 10px 0;
    text-align: center;
  }
  .submit-row {
    margin-top: 1rem;
    padding-left: 1rem;
  }
  h1 {
    margin-bottom: 2rem;
  }
  .card {
    padding-top: 1rem;
    background-color: #f3f3f3;
  }
</style>



// WEBPACK FOOTER //
// src/components/sales-order/view-order-2/OrderSearch.vue